import { createSlice } from '@reduxjs/toolkit';
import initialState from './initialState';
import { applyCompanySettingsPayload } from './utils';
import {
  getCompanySettings,
  getTimezones,
  getWatermarkSettings,
  regenerateApiKey,
  updateCompanySettings,
  updateWatermarkSettings,
  uploadCompanyImages,
} from './service';
import {
  BACK_KEYS,
  BACK_WATERMARK_KEYS as BK_WM_KEYS,
  WATERMARK_STORE_KEYS,
  STORE_KEYS,
  SLICE_SECTIONS,
} from '@/components/CompanyConfiguration/constants';
import saveWatermarkSettingsThunk from './thunks';
import isEmptyLogoUrl from '@/components/CompanyConfiguration/WatermarkSection/utils';

const {
  OPACITY,
  SIZE,
  POSITION,
  TYPE,
  MARGIN_PX,
  WATERMARK_LOGO_URL,
  HAS_SETTINGS,
  NEW_LOGO_FILE,
} = WATERMARK_STORE_KEYS;

const { WATERMARK, LOADING } = SLICE_SECTIONS;

const {
  TIME_ZONE_LIST,
  LOGO,
  LOGO_LOADING,
  WATERMARK_LOGO_LOADING,
  WATERMARK_SETTINGS_LOADING,
  API_KEY,
  REGENERATE_API_KEY_LOADING,
} = STORE_KEYS;

export const CompanySettingsSlice = createSlice({
  name: 'CompanySettings',
  initialState,
  reducers: {
    setSettingsValues(state, action) {
      const { section, key, value } = action.payload;

      if (section) {
        if (!state[section]) state[section] = {};
        state[section][key] = value;
        return;
      }

      state[key] = value;
    },
    setInitialWatermarkSettings(state) {
      const { watermarkSettings } = state;
      // Guardar estado inicial sin watermarkEnabled, newLogoFile y preview
      const { watermarkEnabled, newLogoFile, preview, ...initialSettings } =
        watermarkSettings;
      state.initialWatermarkSettings = initialSettings;
    },
  },
  extraReducers: (builder) => {
    // Company Settings fetching handlers
    builder.addCase(getCompanySettings.fulfilled, (state, action) => {
      applyCompanySettingsPayload(state, action.payload);
    });

    builder.addCase(updateCompanySettings.fulfilled, (state, action) => {
      applyCompanySettingsPayload(state, action.payload);
    });

    // Timezones fetching handlers
    builder.addCase(getTimezones.fulfilled, (state, action) => {
      state[TIME_ZONE_LIST] = action.payload.map((tz) => ({
        value: tz,
        label: tz,
      }));
    });
    builder.addCase(getTimezones.rejected, () => {});
    builder.addCase(getTimezones.pending, () => {});

    // Company settings updating handlers
    builder.addCase(updateWatermarkSettings.fulfilled, (state) => {
      state[WATERMARK][HAS_SETTINGS] = true;
    });

    // Company images uploading handlers
    builder.addCase(uploadCompanyImages.fulfilled, (state, action) => {
      const p = action.payload;
      const { imageType } = action.meta.arg || {};
      const isCompanyLogo = imageType === BACK_KEYS[LOGO];

      const loadingKey = isCompanyLogo ? LOGO_LOADING : WATERMARK_LOGO_LOADING;

      const needUseCompanyLogo =
        !state[WATERMARK][WATERMARK_LOGO_URL] ||
        state[WATERMARK][WATERMARK_LOGO_URL] === state[LOGO];

      if (isCompanyLogo && needUseCompanyLogo) {
        const hasValidLogoUrl =
          p[BACK_KEYS[LOGO]] && !isEmptyLogoUrl(p[BACK_KEYS[LOGO]])
            ? p[BACK_KEYS[LOGO]]
            : null;

        state[WATERMARK][WATERMARK_LOGO_URL] = hasValidLogoUrl;
      }

      if (!isCompanyLogo && p[BACK_KEYS[WATERMARK_LOGO_URL]]) {
        state[WATERMARK][WATERMARK_LOGO_URL] = p[BACK_KEYS[WATERMARK_LOGO_URL]];
      }

      if (!isCompanyLogo && !p[BACK_KEYS[WATERMARK_LOGO_URL]]) {
        const hasValidLogoUrl =
          p[BACK_KEYS[LOGO]] && !isEmptyLogoUrl(p[BACK_KEYS[LOGO]])
            ? p[BACK_KEYS[LOGO]]
            : null;

        state[WATERMARK][WATERMARK_LOGO_URL] =
          hasValidLogoUrl || state[WATERMARK][WATERMARK_LOGO_URL];
      }

      state[LOGO] = p[BACK_KEYS[LOGO]];
      state[LOADING][loadingKey] = false;
    });
    builder.addCase(uploadCompanyImages.pending, (state, action) => {
      const { imageType } = action.meta.arg || {};
      const loadingKey =
        imageType === BACK_KEYS[LOGO] ? LOGO_LOADING : WATERMARK_LOGO_LOADING;
      state[LOADING][loadingKey] = true;
    });
    builder.addCase(uploadCompanyImages.rejected, (state, action) => {
      const { imageType } = action.meta.arg || {};
      const loadingKey =
        imageType === BACK_KEYS[LOGO] ? LOGO_LOADING : WATERMARK_LOGO_LOADING;
      state[LOADING][loadingKey] = false;
    });

    // Watermark settings fetching handlers
    builder.addCase(getWatermarkSettings.fulfilled, (state, action) => {
      const p = action.payload;

      state[WATERMARK][HAS_SETTINGS] =
        p[BK_WM_KEYS[OPACITY]] &&
        p[BK_WM_KEYS[POSITION]] &&
        p[BK_WM_KEYS[TYPE]] &&
        p[BK_WM_KEYS[SIZE]];

      state[WATERMARK][WATERMARK_LOGO_URL] = p.logo;
      state[WATERMARK][OPACITY] =
        p[BK_WM_KEYS[OPACITY]] ?? state[WATERMARK][OPACITY];
      state[WATERMARK][POSITION] =
        p[BK_WM_KEYS[POSITION]] ?? state[WATERMARK][POSITION];
      state[WATERMARK][TYPE] = p[BK_WM_KEYS[TYPE]] ?? state[WATERMARK][TYPE];
      state[WATERMARK][SIZE] = p[BK_WM_KEYS[SIZE]] ?? state[WATERMARK][SIZE];
      state[WATERMARK][MARGIN_PX] =
        p[BK_WM_KEYS[MARGIN_PX]] ?? state[WATERMARK][MARGIN_PX];
      state[WATERMARK][NEW_LOGO_FILE] = null;

      // Save initial watermark settings for change detection
      const { watermarkEnabled, newLogoFile, preview, ...initialSettings } =
        state[WATERMARK];
      state.initialWatermarkSettings = initialSettings;
    });
    builder.addCase(getWatermarkSettings.rejected, () => {});
    builder.addCase(getWatermarkSettings.pending, () => {});
    builder.addCase(saveWatermarkSettingsThunk.pending, (state) => {
      state[LOADING][WATERMARK_SETTINGS_LOADING] = true;
    });
    builder.addCase(saveWatermarkSettingsThunk.fulfilled, (state) => {
      state[LOADING][WATERMARK_SETTINGS_LOADING] = false;
      // Clear newLogoFile after successful save
      state[WATERMARK][NEW_LOGO_FILE] = null;
      // Reset initial watermark settings after successful save
      const { watermarkEnabled, newLogoFile, preview, ...initialSettings } =
        state[WATERMARK];
      state.initialWatermarkSettings = initialSettings;
    });
    builder.addCase(saveWatermarkSettingsThunk.rejected, (state) => {
      state[LOADING][WATERMARK_SETTINGS_LOADING] = false;
    });

    // API key regeneration handlers
    builder.addCase(regenerateApiKey.pending, (state) => {
      state[LOADING][REGENERATE_API_KEY_LOADING] = true;
    });
    builder.addCase(regenerateApiKey.fulfilled, (state, action) => {
      state[API_KEY] = action.payload?.api_key ?? state[API_KEY];
      state[LOADING][REGENERATE_API_KEY_LOADING] = false;
    });
    builder.addCase(regenerateApiKey.rejected, (state) => {
      state[LOADING][REGENERATE_API_KEY_LOADING] = false;
    });
  },
});

export const { setSettingsValues, setInitialWatermarkSettings } =
  CompanySettingsSlice.actions;

export default CompanySettingsSlice.reducer;
